import { Form, Formik } from 'formik';
import React, { FC, useEffect, useMemo, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { Trans, useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { audkenniLogin, checkLoginCredentials } from '../../../actions/userProfileActions';
import IMyShipmentsViewModel from '../../../interfaces/myShipments/myShipments';
import IUserProfileReducer from '../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../reducers';
import { RootState } from '../../../store';
import Button from '../../base/button';
import { StyledErrorMessage } from '../../base/forms/formikFieldWrapper.styles';
import { Input } from '../../base/forms/input';
import { Label } from '../../base/forms/label';
import { A, H2, H4, P } from '../../base/typography';
import { ReactComponent as LoginImage } from './login-minar-sidur.svg';
import {
	audkenniAppLoginInitialValues,
	audkenniAppLoginSchema,
	userAndPassInitialValues,
	userAndPassSchema,
} from './login.schema';
import * as StyledLogin from './login.styles';

interface Props {
	page: IMyShipmentsViewModel;
}

enum TabEnum {
	ElectronicId = 0,
	AudkenniApp = 1,
	Password = 2,
}

const LOGIN_TAB_HASH_KEY = 'loginTab';

const LOGIN_TAB_HASH_VALUES = {
	[TabEnum.ElectronicId]: 'electronic-id',
	[TabEnum.AudkenniApp]: 'audkenni-app',
	[TabEnum.Password]: 'password',
};

const getActiveTabFromHash = (hash: string): TabEnum | null => {
	const hashValue = new URLSearchParams(hash.replace(/^#/, '')).get(LOGIN_TAB_HASH_KEY);

	if (hashValue === LOGIN_TAB_HASH_VALUES[TabEnum.AudkenniApp]) return TabEnum.AudkenniApp;
	if (hashValue === LOGIN_TAB_HASH_VALUES[TabEnum.Password]) return TabEnum.Password;
	if (hashValue === LOGIN_TAB_HASH_VALUES[TabEnum.ElectronicId]) return TabEnum.ElectronicId;

	return null;
};

const Login: FC<Props> = () => {
	const { t } = useTranslation('myShipments');
	const dispatch = useDispatch<any>();
	const history = useHistory();
	const location = useLocation();
	const prefilledEmail = useMemo(() => new URLSearchParams(location.search).get('email') || '', [location.search]);
	const [value, setValue] = useState('');
	const [username, setUsername] = useState(prefilledEmail);
	const [password, setPassword] = useState('');
	const [socialId, setSocialId] = useState<string>('');
	const activeTab = useMemo(
		() => getActiveTabFromHash(location.hash) ?? (prefilledEmail ? TabEnum.Password : TabEnum.ElectronicId),
		[location.hash, prefilledEmail]
	);

	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { settings } = useSelector((store: RootState) => store.pageReducer);
	const { loginLoading, loginError, loginErrorMessage, audkenniVerificationCode } = userProfileReducer;

	const onTabClick = (tabId: TabEnum) => {
		if (loginLoading) return;

		const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));
		hashParams.set(LOGIN_TAB_HASH_KEY, LOGIN_TAB_HASH_VALUES[tabId]);
		const nextHash = `#${hashParams.toString()}`;

		if (location.hash !== nextHash) {
			history.push({
				pathname: location.pathname,
				search: location.search,
				hash: nextHash,
			});
		}
	};

	const login = () => {
		if (!loginLoading) dispatch(checkLoginCredentials(username, password));
	};

	useEffect(() => {
		if (prefilledEmail) {
			setUsername(prefilledEmail);
		}
	}, [prefilledEmail]);

	const loginWithAudkenni = async (values): Promise<void> => {
		const useAudkenniApp = activeTab === TabEnum.AudkenniApp;
		const ssnOrPhone =
			useAudkenniApp
				? socialId
				: values.phoneNumber.replaceAll(' ', '').replaceAll('-', '').replaceAll('/', '');
				
		dispatch(audkenniLogin(ssnOrPhone, useAudkenniApp))
	};

	const parseLoginError = (error: string): string => {
		// loginError can be set without a message (network failures, aborted requests), so fall back to
		// the generic copy rather than crashing the whole login page.
		if (!error) return t('page-login.loginFailed');
		if (error.startsWith('page-login.')) return t(error);
		if (error.includes('Villa kom upp í innskráningu með rafrænum skilríkjum') ||
			error.includes('Error encountered while logging in with e-certificate')){
			return error.slice(error.lastIndexOf(': ')+1, error.length)
		}
		let err = t('page-login.loginFailed');
		if (error === 'invalid_grant') err = t('page-login.invalidGrant');
		if (error === 'identificationNumberAndEmailIsNotConfirmed' || error === 'emailIsNotConfirmed')
			err = t('page-login.emailVerificationMissing');
		if (error === 'identificationNumberIsNotConfirmed') err = t('page-login.socialIdVerificationMissing');
		if (error === 'user_fyrtaekjasidur') err = t('page-login.user_fyrtaekjasidur');
		if (error === 'user_fyrtaekjasidur1') err = t('page-login.user_fyrtaekjasidur1');
		if (error === 'user_fyrtaekjasidur2') err = t('page-login.user_fyrtaekjasidur2');
		return err;
	};

	return (
		<StyledLogin.Wrapper>
			<StyledLogin.HeadingImage image="/media/v3hgcyy0/30.png?mode=max" hidePlaceholder />
			<StyledLogin.LoginModalWrapper>
				<H2>{settings.Values.LoginPageHeading}</H2>
				{/* <StyledLogin.Attention>
					<FaExclamationCircle />
					{t('attentionRegistrationRequired')}
				</StyledLogin.Attention> */}
				<P>{settings.Values.LoginPageSubtext}</P>
				{/* <P>
					{t('Click')}{' '}
					<A onClick={() => dispatch(setActiveModal(COMPANY_SERVICE_PORTAL_MODAL))} target="_blank">
						{t('here')}
					</A>{' '}
					{t('companyLogin')}
				</P> */}
				<StyledLogin.Tabs login>
					<StyledLogin.Tab
						login
						active={activeTab === TabEnum.ElectronicId}
						onClick={onTabClick.bind(null, TabEnum.ElectronicId)}
						disabled={loginLoading}>
						{t('page-login.electronicId')}
					</StyledLogin.Tab>
					<StyledLogin.Tab
						login
						active={activeTab === TabEnum.AudkenniApp}
						onClick={onTabClick.bind(null, TabEnum.AudkenniApp)}
						disabled={loginLoading}>
						{t('page-login.audkenni.audkenniApp')}
					</StyledLogin.Tab>
					<StyledLogin.Tab
						login
						active={activeTab === TabEnum.Password}
						onClick={onTabClick.bind(null, TabEnum.Password)}
						disabled={loginLoading}>
						{t('page-login.password')}
					</StyledLogin.Tab>
				</StyledLogin.Tabs>
				<AnimateHeight
					duration={400}
					style={{ opacity: activeTab === TabEnum.ElectronicId ? 1 : 0 }}
					height={activeTab === TabEnum.ElectronicId ? 'auto' : 0}>
					<Formik
						onSubmit={loginWithAudkenni}
						initialValues={{ phoneNumber: '' }}
						validationSchema={yup.object().shape({
							phoneNumber: yup
								.string()
								.matches(/^[6-8][0-9]{2}[- ]?[0-9]{4}$/)
								.required('required'),
						})}>
						{({ errors, touched, setFieldValue }) => {
							return (
								<Form>
									<Label>{t('page-login.electronicIdHeading')}</Label>
									{loginLoading ? (
										<div style={{ textAlign: 'center', paddingBottom: '2rem' }}>
											<p>
												<Trans
													t={t as TFunction}
													i18nKey="page-login.audkenni.verifyText"
													components={[
														<span style={{ textDecoration: 'underline' }} key={null} />,
													]}
												/>
											</p>
											<H4>{audkenniVerificationCode}</H4>
										</div>
									) : (
										<Input
											name="phoneNumber"
											placeholder={t('page-login.electronicIdPlaceholder')}
											value={value}
											type="tel"
											errors={errors.phoneNumber}
											touched={touched.phoneNumber}
											onChange={e => {
												let { value: newValue } = e.target;

												// Remove non-numeric characters except for hyphen
												newValue = newValue.replace(/[^\d-]/g, '');

												// Remove any hyphens that are not at the 4th position
												if (newValue.indexOf('-') !== -1 && newValue.indexOf('-') !== 3) {
													newValue = newValue.replace(/-/g, '');
												}

												// Apply the phone number format
												if (newValue.length > 3) {
													newValue =
														newValue.slice(0, 3) +
														'-' +
														newValue.slice(3).replace(/-/g, '');
												}

												// Prevent multiple hyphens
												if (newValue.length > 8) {
													newValue = newValue.slice(0, 8);
												}

												setFieldValue('phoneNumber', newValue);

												setValue(newValue);
											}}
										/>
									)}
									<StyledErrorMessage>
										{touched.phoneNumber &&
											errors &&
											errors.phoneNumber &&
											t('page-login.phoneNumberNotCorrect')}
									</StyledErrorMessage>
									<StyledLogin.ButtonWrapper>
										<Button primary mobileBlock type="submit" isLoading={loginLoading}>
											{t('page-login.logIn')}
										</Button>
										{loginError && (
											<StyledErrorMessage>
												{parseLoginError(loginErrorMessage)}
											</StyledErrorMessage>
										)}
									</StyledLogin.ButtonWrapper>
								</Form>
							);
						}}
					</Formik>
				</AnimateHeight>
				<AnimateHeight
					duration={400}
					style={{ opacity: activeTab === TabEnum.AudkenniApp ? 1 : 0 }}
					height={activeTab === TabEnum.AudkenniApp ? 'auto' : 0}>
					<Formik
						onSubmit={loginWithAudkenni}
						initialValues={audkenniAppLoginInitialValues}
						validationSchema={audkenniAppLoginSchema}>
						{({ errors, touched, setFieldValue }) => {
							return (
								<Form>
									{loginLoading ? (
										<div style={{ textAlign: 'center', paddingBottom: '2rem' }}>
											<p>
												<Trans
													t={t as TFunction}
													i18nKey="page-login.audkenni.verifyText"
													components={[
														<span style={{ textDecoration: 'underline' }} key={null} />,
													]}
												/>
											</p>
											<H4>{audkenniVerificationCode}</H4>
										</div>
									) : (
										// Show SSN input if loginLoading is false
										<>
											<Label>{t('generalForm:ssn')}</Label>
											<Input
												id="ssn"
												type="text"
												name="ssn"
												placeholder={t('generalForm:ssn')}
												value={socialId}
												onChange={e => {
													if (e.target.value === '' || /^\d+$/.test(e.target.value)) {
														setSocialId(e.target.value);
														setFieldValue('ssn', e.target.value);
													}
												}}
												touched={touched.ssn}
												errors={errors.ssn}
											/>
										</>
									)}
									<StyledLogin.ButtonWrapper>
										<Button primary mobileBlock type="submit" isLoading={loginLoading}>
											{t('page-login.logIn')}
										</Button>
										{loginError && (
											<StyledErrorMessage>
												{parseLoginError(loginErrorMessage)}
											</StyledErrorMessage>
										)}
										<StyledErrorMessage>
											{touched.ssn && errors && errors.ssn && <>t(errors.ssn)</>}
										</StyledErrorMessage>

										{loginError && loginErrorMessage === 'user_fyrtaekjasidur' && (
											<StyledErrorMessage>
												{parseLoginError('user_fyrtaekjasidur1')}
												<a href="https://fyrirtaeki.posturinn.is/">
													{t('page-login.user_fyrtaekjasidurLinkFyritaekjasidur')}
												</a>
												{parseLoginError('user_fyrtaekjasidur2')}
												<a href="https://posturinn.is/minar-sidur/">
													{t('page-login.user_fyrtaekjasidurLinkMinarSidur')}
												</a>
											</StyledErrorMessage>
										)}
									</StyledLogin.ButtonWrapper>
								</Form>
							);
						}}
					</Formik>
				</AnimateHeight>
				<AnimateHeight
					duration={400}
					style={{ opacity: activeTab === TabEnum.Password ? 1 : 0 }}
					height={activeTab === TabEnum.Password ? 'auto' : 0}>
					<Formik
						onSubmit={login}
						initialValues={userAndPassInitialValues}
						validationSchema={userAndPassSchema}>
						<Form>
							<Label>{t('email')}</Label>
							<Input
								id="email"
								name="email"
								type="email"
								autoComplete="email"
								placeholder={t('page-login.enterEmail')}
								value={username}
								onChange={e => setUsername(e.target.value)}
							/>
							<Label>{t('page-login.password')}</Label>
							<Input
								id="password"
								name="password"
								type="password"
								autoComplete="current-password"
								placeholder={t('page-login.enterPassword')}
								value={password}
								onChange={e => setPassword(e.target.value)}
							/>
							<StyledLogin.ButtonWrapper>
								<Button primary mobileBlock type="submit" isLoading={loginLoading}>
									{t('page-login.logIn')}
								</Button>
								{loginError && (
									<StyledErrorMessage>{parseLoginError(loginErrorMessage)}</StyledErrorMessage>
								)}

								{loginError && loginErrorMessage === 'user_fyrtaekjasidur' && (
									<StyledErrorMessage>
										{parseLoginError('user_fyrtaekjasidur1')}
										<a href="https://fyrirtaeki.posturinn.is/">
											{t('page-login.user_fyrtaekjasidurLinkFyritaekjasidur')}
										</a>
										{parseLoginError('user_fyrtaekjasidur2')}
										<a href="https://posturinn.is/minar-sidur/">
											{t('page-login.user_fyrtaekjasidurLinkMinarSidur')}
										</a>
									</StyledErrorMessage>
								)}
							</StyledLogin.ButtonWrapper>
							<StyledLogin.RegisterWrapper>
								<A to={settings.Values.ForgotPassword.Url}>{t('page-login.forgot-password')}</A>
								<A to={settings.Values.Registration.Url}>{t('page-login.register')}</A>
							</StyledLogin.RegisterWrapper>
						</Form>
					</Formik>
				</AnimateHeight>
			</StyledLogin.LoginModalWrapper>
			<StyledLogin.SideWrapper>
				<LoginImage />
				{/*
				<H2>{settings.Values.LoginPageAdvertisementHeading}</H2>
				<P>{settings.Values.LoginPageAdvertisementText}</P>

				App buttons are commented out in case they need to be added again.
				<StyledLogin.AppButtons>
					<StyledLogin.AppButton to={settings.Values.PlayStore.Url} target={settings.Values.PlayStore.Target}>
						<FaGooglePlay />
						<StyledLogin.AppButtonText>
							<StyledLogin.AppButtonMainText>{t('page-login.download')}</StyledLogin.AppButtonMainText>
							<StyledLogin.AppButtonSubtext>
								{t('page-login.fromGooglePlay')}
							</StyledLogin.AppButtonSubtext>
						</StyledLogin.AppButtonText>
					</StyledLogin.AppButton>
					<StyledLogin.AppButton to={settings.Values.AppStore.Url} target={settings.Values.AppStore.Target}>
						<FaApple />
						<StyledLogin.AppButtonText>
							<StyledLogin.AppButtonMainText>{t('page-login.download')}</StyledLogin.AppButtonMainText>
							<StyledLogin.AppButtonSubtext>{t('page-login.fromAppStore')}</StyledLogin.AppButtonSubtext>
						</StyledLogin.AppButtonText>
					</StyledLogin.AppButton>
				</StyledLogin.AppButtons> */}
			</StyledLogin.SideWrapper>
			{/*
				Mobile demo is commented out in case it needs to be added again.
				<StyledLogin.MobileDemo>
					<StyledLogin.MobileContent>
						<video autoPlay muted loop src={settings.Values.LoginVideo.Src} />
					</StyledLogin.MobileContent>
				</StyledLogin.MobileDemo>
			*/}
		</StyledLogin.Wrapper>
	);
};

export default Login;
